import { Link } from 'react-router';

import ArticleCardInteractionEvent from '~/events/ArticleCardInteractionEvent';
import useArticleCardImpression from '~/hooks/useArticleCardImpression';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { appendTrailingSlash } from '~/utils/trailingSlash';

import SearchResultThumbnail from './SearchResultThumbnail';

const MODULE_TYPE = 'search-panel-articles';

type ArticleHit = {
  canonical_path?: string;
  images?: {
    featured?: { alt?: string; original_img_url?: string };
  };
  name: string;
  objectID: string;
  slug: string;
  tracking_id?: number | string;
};

type Props = {
  hit: ArticleHit;
  insightsFields?: Record<string, unknown>;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  position: number;
  query?: string;
};

function getPath({ canonical_path, slug }: ArticleHit) {
  return appendTrailingSlash(
    canonical_path ? canonical_path : `/news/${slug}/`
  );
}

export default function ArticleSearchResult({
  onClick,
  insightsFields = {},
  hit,
  position,
  query
}: Props) {
  const { isBot } = useRootData();
  const user = useUser();
  const impressionRef = useArticleCardImpression<HTMLAnchorElement>({
    articleId: hit.tracking_id?.toString(),
    moduleType: MODULE_TYPE,
    position
  });

  const {
    name,
    images: {
      featured: { alt: featuredAlt, original_img_url } = {
        alt: '',
        original_img_url: ''
      }
    } = {},
    objectID
  } = hit;

  const image = original_img_url ? (
    <SearchResultThumbnail
      alt={featuredAlt || name}
      src={original_img_url}
      isSquare
    />
  ) : (
    ''
  );

  const link = {
    pathname: getPath(hit),
    state: { hit, insightsFields }
  };

  return (
    <Link
      onClick={(event) => {
        new ArticleCardInteractionEvent({
          articleId: hit.tracking_id?.toString(),
          gesture: 'click',
          guestId: user.guestId,
          isBot,
          moduleType: MODULE_TYPE,
          position,
          searchTerm: query
        }).send();

        if (onClick) onClick(event);
      }}
      to={link}
      className="mb-3 flex h-16 items-start font-semibold"
      ref={impressionRef}
      rel="article"
    >
      <div className="flex size-16 items-center mix-blend-multiply">
        {image}
      </div>
      <section className="my-auto ml-5 w-4/5">
        <h5
          className="line-clamp-3 font-heading text-sm font-normal"
          id={`aria-article-title--${objectID}`}
        >
          {name}
        </h5>
      </section>
    </Link>
  );
}
